import { createContext, useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const Ctx = createContext(null);
const LS_KEY = "eyw_active_context";

// The catalog of workspaces ("roles") a context can point at. `scoped` roles
// operate on a single center; `allowAll` roles may also target an all-centers view.
export const ROLES = {
  admin: { key: "admin", label: "Admin", short: "Admin", route: "/admin", scoped: false },
  council: { key: "council", label: "Council", short: "Council", route: "/council", scoped: false },
  manager: { key: "manager", label: "Center Manager", short: "Center Manager", route: "/manager", scoped: true, allowAll: true },
  frontdesk: { key: "frontdesk", label: "Front Desk", short: "Front Desk", route: "/pos", scoped: true, allowAll: false },
  enhancer: { key: "enhancer", label: "Enhancer Studio", short: "Enhancer", route: "/enhancer", scoped: false },
  member: { key: "member", label: "My Dashboard", short: "Dashboard", route: "/dashboard", scoped: false },
};

// Which workspaces the signed-in user is entitled to — mirrors the server-side
// permission model (single role + managed-center + enhancer capability).
function availableRoleKeys(user) {
  if (!user) return [];
  const r = user.role;
  const out = [];
  if (["admin", "staff"].includes(r)) out.push("admin");
  if (r === "council") out.push("council");
  if (["admin", "staff", "location_manager"].includes(r) || user.manages_location_id) out.push("manager");
  // Enhancer Studio — enhancers/vendors, plus admin/staff (backend enhancer_dep allows these roles).
  if (["enhancer", "vendor", "admin", "staff"].includes(r)) out.push("enhancer");
  if (["admin", "staff", "location_manager"].includes(r)) out.push("frontdesk");
  if (!["admin", "staff", "council", "location_manager", "enhancer"].includes(r)) out.push("member");
  return out;
}

export function ActiveContextProvider({ children }) {
  const { user } = useAuth();
  const roleKeys = useMemo(() => availableRoleKeys(user), [user]);
  const canScope = roleKeys.includes("manager") || roleKeys.includes("frontdesk");

  const centersQ = useQuery({
    queryKey: ["ctx-centers"],
    queryFn: () => api.get("/manager/centers").then((r) => r.data),
    enabled: !!user && canScope,
    staleTime: 5 * 60 * 1000,
  });
  const centers = centersQ.data || [];

  const [state, setState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
  });

  const activeRole = roleKeys.includes(state.role) ? state.role : (roleKeys[0] || null);

  const activeCenterId = useMemo(() => {
    const saved = state.centerId;
    if (saved === "__all__" || (saved && centers.some((c) => c.id === saved))) return saved;
    return centers.length ? centers[0].id : "";
  }, [state.centerId, centers]);

  const activeCenterName = activeCenterId === "__all__"
    ? "All Centers"
    : (centers.find((c) => c.id === activeCenterId)?.name || "");

  // Apply the scoping header synchronously so any query fired this render is scoped.
  // Harmless for non-admins (the server only honours it for admin/staff).
  if (activeCenterId) api.defaults.headers.common["X-Manage-Location"] = activeCenterId;
  else delete api.defaults.headers.common["X-Manage-Location"];

  const persist = (next) => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    setState(next);
  };
  const setActiveRole = (roleKey) => persist({ ...state, role: roleKey });
  const setActiveCenter = (id) => persist({ ...state, centerId: id });
  const setContext = (roleKey, id) => persist({ role: roleKey, centerId: id ?? state.centerId });

  const value = {
    user, roles: ROLES, roleKeys, centers, canScope,
    centersLoading: centersQ.isLoading,
    activeRole, activeCenterId, activeCenterName,
    setActiveRole, setActiveCenter, setContext,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useActiveContext = () => useContext(Ctx);
